import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Brief triage status, shared through the backend (GET/PUT /api/inbox/state).
 *
 * Statuses (backend vocabulary):
 *   'new'       default; nobody has triaged the brief yet
 *   'triage'    someone explicitly started working it (never set by viewing)
 *   'acked'     acknowledged, stays in the open queue
 *   'shipped'   detection rules were exported (shown as "Rules exported")
 *   'archived'  hidden from the open queue
 *
 * One module-level store backs every hook instance, so the queue, the
 * reading pane and the rail always agree. Writes are optimistic: the store
 * changes first, and a failed request rolls back and rejects so the caller
 * can report it.
 *
 * Earlier builds kept statuses in this browser's localStorage
 * ('brief_status_v1'). The first successful load imports them once
 * (POST /api/inbox/state/import never overwrites server state), then the
 * key is removed and localStorage is no longer used for status.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_KEY = 'brief_status_v1';
const BULK_MAX = 500; // backend limit per bulk request

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

let store = {
  token: null,
  items: {}, // attack_id -> { status, updated_at, updated_by }
  loaded: false,
  loading: false,
  error: null,
};
let inflight = null;
const listeners = new Set();

function emit(patch) {
  store = { ...store, ...patch };
  listeners.forEach((fn) => fn(store));
}

function setItems(updates) {
  const items = { ...store.items };
  Object.entries(updates).forEach(([id, item]) => {
    if (!item || item.status === 'new') delete items[id];
    else items[id] = item;
  });
  emit({ items });
}

function readLegacy() {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

async function migrateLegacy() {
  const legacy = readLegacy();
  if (!legacy) return false;
  const items = {};
  Object.entries(legacy).forEach(([id, status]) => {
    if (typeof status === 'string' && status !== 'unread' && status !== 'new') items[id] = status;
  });
  if (Object.keys(items).length) {
    await axios.post(`${API}/inbox/state/import`, { items }, { headers: authHeaders() });
  }
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  return Object.keys(items).length > 0;
}

/** Fetch the shared state. Safe to call often; concurrent calls share one request. */
export function loadBriefStatus() {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve();
  if (store.token !== token) {
    store = { token, items: {}, loaded: false, loading: false, error: null };
  }
  if (inflight) return inflight;
  emit({ loading: true });
  inflight = (async () => {
    try {
      if (!store.loaded) {
        // A failed import is retried on the next load; it never blocks reading state.
        try { await migrateLegacy(); } catch { /* keep the legacy key for next time */ }
      }
      const res = await axios.get(`${API}/inbox/state`, { headers: authHeaders() });
      emit({ items: res.data?.items || {}, loaded: true, loading: false, error: null });
    } catch (error) {
      emit({ loading: false, error });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export default function useBriefStatus() {
  const [snap, setSnap] = useState(store);

  useEffect(() => {
    listeners.add(setSnap);
    setSnap(store);
    if (!store.loaded && !inflight) loadBriefStatus();
    return () => { listeners.delete(setSnap); };
  }, []);

  const getStatus = useCallback(
    (id) => (id != null && snap.items[id]?.status) || 'new',
    [snap.items]
  );
  const getItem = useCallback((id) => (id != null ? snap.items[id] || null : null), [snap.items]);

  const setStatus = useCallback(async (id, status) => {
    if (id == null) return;
    const previous = store.items[id] || null;
    setItems({ [id]: { status, updated_at: new Date().toISOString(), updated_by: null } });
    try {
      const res = await axios.put(`${API}/inbox/state/${encodeURIComponent(id)}`, { status }, { headers: authHeaders() });
      setItems({ [id]: res.data });
    } catch (err) {
      setItems({ [id]: previous });
      throw err;
    }
  }, []);

  /** Set one status on many briefs. Resolves to the number the server updated. */
  const markMany = useCallback(async (ids, status) => {
    const list = ids.filter((id) => id != null);
    if (!list.length) return 0;
    const previous = {};
    const optimistic = {};
    const now = new Date().toISOString();
    list.forEach((id) => {
      previous[id] = store.items[id] || null;
      optimistic[id] = { status, updated_at: now, updated_by: null };
    });
    setItems(optimistic);
    try {
      let updated = 0;
      for (let i = 0; i < list.length; i += BULK_MAX) {
        const res = await axios.post(
          `${API}/inbox/state/bulk`,
          { attack_ids: list.slice(i, i + BULK_MAX), status },
          { headers: authHeaders() }
        );
        updated += res.data?.updated || 0;
      }
      loadBriefStatus(); // pick up server timestamps and actor
      return updated;
    } catch (err) {
      setItems(previous);
      throw err;
    }
  }, []);

  return {
    getStatus,
    getItem,
    setStatus,
    markMany,
    statusMap: snap.items,
    loaded: snap.loaded,
    loading: snap.loading,
    error: snap.error,
    reload: loadBriefStatus,
  };
}

/** Inbox wording. 'shipped' is an export, not a SIEM deployment, so say so. */
export const STATUS_LABELS = {
  new:      'New',
  triage:   'In triage',
  acked:    'Acknowledged',
  shipped:  'Rules exported',
  archived: 'Archived',
};

export const STATUS_ORDER = ['new', 'triage', 'acked', 'shipped', 'archived'];
